import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserHash, saveCredentials, checkExistingCredentials } from '../services/bricksetAuthApi';
import Toast from '../components/Toast';
import '../styles/RebrickableSetupPage.css';

const BricksetSetupPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [password, setPassword] = useState('');
  const [userHash, setUserHash] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Check for existing credentials on mount
  useEffect(() => {
    const checkCredentials = async () => {
      if (!token) return;

      try {
        const result = await checkExistingCredentials(token);
        setHasExistingCredentials(result.hasBoth);
      } catch (error) {
        console.error('Error checking existing credentials:', error);
      }
    };

    checkCredentials();
  }, [token]);

  // Section completion states
  const isStep1Complete = username.trim() !== '';
  const isStep2Complete = isStep1Complete && apiKey.trim() !== '';
  const isStep3Complete = isStep2Complete && userHash !== '';

  // Handle getting user hash from Brickset
  const handleVerifyPassword = async () => {
    if (!username || !password || !apiKey) return;

    setLoading(true);
    setVerifyError(null);

    try {
      // Get user hash
      const obtainedHash = await getUserHash(username.trim(), password, apiKey.trim());
      setUserHash(obtainedHash);
      setPassword(''); // Clear password from memory
      setVerifyError(null);
    } catch (error) {
      setVerifyError(error instanceof Error ? error.message : 'Failed to verify password');
      setUserHash(''); // Clear hash if verification failed
    } finally {
      setLoading(false);
    }
  };

  // Handle saving credentials
  const handleSave = async () => {
    if (!token || !apiKey || !userHash) return;

    // Show warning if user already has credentials
    if (hasExistingCredentials && !showOverwriteWarning) {
      setShowOverwriteWarning(true);
      return;
    }

    setLoading(true);
    setSaveError(null);

    try {
      await saveCredentials(apiKey.trim(), userHash, token);
      setToast({
        message: 'Credentials saved successfully! Redirecting...',
        type: 'success'
      });

      // Redirect to settings page after 2 seconds
      setTimeout(() => {
        navigate('/settings');
      }, 2000);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOverwrite = () => {
    setShowOverwriteWarning(false);
  };

  const handleConfirmOverwrite = () => {
    setShowOverwriteWarning(false);
    handleSave();
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="rebrickable-setup-page">
      <div className="setup-header">
        <h1>Brickset Connection Setup</h1>
        <button className="back-button" onClick={handleBack} title="Go back">
          ✕
        </button>
      </div>

      <div className="setup-container">
        {/* Step 1: Create Account */}
        <div className="setup-section expanded">
          <div className="section-header">
            <div className="section-number">1</div>
            <h2>Create Brickset Account</h2>
            {isStep1Complete && <span className="section-status complete">✓</span>}
          </div>
          <div className="section-content">
            <p>
              If you don't have a Brickset account yet, create one here:{' '}
              <a href="https://brickset.com/signup" target="_blank" rel="noopener noreferrer">
                brickset.com/signup
              </a>
            </p>
            <div className="form-group">
              <label htmlFor="username">Brickset Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Step 2: Get API Key */}
        <div className={`setup-section ${isStep1Complete ? 'expanded' : 'locked'}`}>
          <div className="section-header">
            <div className="section-number">2</div>
            <h2>Get API Key</h2>
            {isStep2Complete && <span className="section-status complete">✓</span>}
            {!isStep1Complete && <span className="section-status locked">🔒</span>}
          </div>
          {isStep1Complete && (
            <div className="section-content">
              <p>
                Request your API key from Brickset:{' '}
                <a
                  href="https://brickset.com/tools/webservices/requestkey"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  brickset.com/tools/webservices/requestkey
                </a>.
                You should receive the API key via email within a few minutes.
              </p>
              <div className="form-group">
                <label htmlFor="apiKey">API Key</label>
                <input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                  className="form-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Step 3: Authenticate with Password */}
        <div className={`setup-section ${isStep2Complete ? 'expanded' : 'locked'}`}>
          <div className="section-header">
            <div className="section-number">3</div>
            <h2>Authenticate with Password</h2>
            {isStep3Complete && <span className="section-status complete">✓</span>}
            {!isStep2Complete && <span className="section-status locked">🔒</span>}
          </div>
          {isStep2Complete && (
            <div className="section-content">
              <div className="security-notice">
                🔒 <strong>Security Note:</strong> Your password is sent through our backend to Brickset's API and is never stored.
                The password is used once to obtain a user hash and is immediately discarded.
                The user hash will allow us to perform actions on your behalf, such as managing your collection and wishlist.
              </div>
              {verifyError && (
                <div className="error-message">
                  ✕ {verifyError}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="password">Brickset Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="form-input"
                  disabled={isStep3Complete}
                />
              </div>
              {!isStep3Complete && (
                <button
                  className="verify-button"
                  onClick={handleVerifyPassword}
                  disabled={loading || !password}
                >
                  {loading ? 'Verifying...' : 'Verify Password'}
                </button>
              )}
              {isStep3Complete && (
                <div className="success-message">
                  ✓ Successfully authenticated as <strong>{username}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 4: Save Credentials */}
        <div className={`setup-section ${isStep3Complete ? 'expanded' : 'locked'}`}>
          <div className="section-header">
            <div className="section-number">4</div>
            <h2>Save Credentials</h2>
            {!isStep3Complete && <span className="section-status locked">🔒</span>}
          </div>
          {isStep3Complete && (
            <div className="section-content">
              <p>Your Brickset credentials are ready to be saved.</p>
              {saveError && (
                <div className="error-message">
                  ✕ {saveError}
                </div>
              )}
              <button
                className="save-button"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Credentials'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overwrite Warning Modal */}
      {showOverwriteWarning && (
        <div className="modal-overlay" onClick={handleCancelOverwrite}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Overwrite Existing Credentials?</h3>
            <p>
              You already have Brickset credentials saved. Saving these new values will overwrite the existing ones.
            </p>
            <div className="modal-actions">
              <button className="button-cancel" onClick={handleCancelOverwrite}>
                Cancel
              </button>
              <button className="button-confirm" onClick={handleConfirmOverwrite}>
                Overwrite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default BricksetSetupPage;
